import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail, Phone, ShieldCheck } from 'lucide-react';
import { LOGO_ICON_BLUE } from '@/components/SplashScreen';

export default function DeleteData() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Back to home"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <img src={LOGO_ICON_BLUE} alt="Rebuilt" className="h-8 w-8" />
          <h1 className="text-xl font-bold text-gray-900">Delete Data</h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <Trash2 className="h-8 w-8 text-red-600" />
            <h2 className="text-3xl font-bold text-gray-900">Request Data Deletion</h2>
          </div>

          <p className="text-sm text-gray-500 mb-8">
            <strong>Last Updated:</strong> April 2026
          </p>

          <div className="prose prose-gray max-w-none space-y-8" data-testid="delete-data-content">

            {/* Intro */}
            <section>
              <p className="text-gray-700 leading-relaxed text-lg">
                If you would like Rebuilt to delete personal data associated with your account,
                please email us at{' '}
                <a
                  href="mailto:support@rebuiltindia.co?subject=Data%20Deletion%20Request"
                  className="text-blue-600 font-semibold hover:underline"
                  data-testid="support-email-link"
                >
                  support@rebuiltindia.co
                </a>{' '}
                using your registered contact details.
              </p>
            </section>

            {/* What to include */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">What to Include in Your Request</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Please include the following details in your email so we can verify and process your request:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Full name</li>
                <li>Registered mobile number</li>
                <li>Registered email address (if applicable)</li>
                <li>Details of the deletion request (e.g. which data you want removed)</li>
              </ul>
            </section>

            {/* Verification */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
                Identity Verification
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We may verify your identity before processing the request. This is to protect your
                data from unauthorised deletion attempts.
              </p>
            </section>

            {/* Timeline */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Processing Timeline</h3>
              <p className="text-gray-700 leading-relaxed">
                Most requests are completed within <strong>7 business days</strong> of verification.
                You will receive a confirmation email once the requested data has been deleted.
              </p>
            </section>

            {/* Exceptions */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Data Retention Exceptions</h3>
              <p className="text-gray-700 leading-relaxed">
                Certain information may be retained where required by law, fraud prevention,
                security, audits, or regulatory obligations. Retained data is kept only for the
                minimum period required and is not used for any other purpose.
              </p>
            </section>

            {/* Contact callout */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h3>
              <div className="bg-blue-50 border border-blue-100 p-5 rounded-lg">
                <p className="text-gray-800 flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <strong>Email:</strong>{' '}
                  <a
                    href="mailto:support@rebuiltindia.co"
                    className="text-blue-600 hover:underline"
                  >
                    support@rebuiltindia.co
                  </a>
                </p>
                <p className="text-gray-800 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <strong>Phone:</strong>{' '}
                  <a href="tel:7666020197" className="text-blue-600 hover:underline">
                    7666 020 197
                  </a>
                </p>
              </div>
            </section>
          </div>

          {/* CTA buttons */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row gap-3">
            <a
              href="mailto:support@rebuiltindia.co?subject=Data%20Deletion%20Request&body=Full%20name%3A%20%0ARegistered%20mobile%20number%3A%20%0ARegistered%20email%20address%3A%20%0ADetails%20of%20deletion%20request%3A%20%0A%0AI%20would%20like%20to%20delete%20personal%20data%20associated%20with%20my%20Rebuilt%20account."
              className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-center"
              data-testid="cta-email-delete-data"
            >
              Email Data Deletion Request
            </a>
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-6 py-3 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
